import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, CheckCircle2, Loader2, FileText, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch (_e) { return iso; }
};

/**
 * Public page (no auth) opened by the sous-traitant from an email link.
 * Displays the list of docs to renew + a simple upload form per doc.
 * Docs land automatically into the CRM (replaces the previous manual doc).
 */
export default function StRenewalPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-doc-type state
  const [uploads, setUploads] = useState({}); // { [doc_type]: {file, editionDate, sending, done} }

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/st-renewal/${token}`);
      setInfo(r.data);
      document.title = `Renouvellement — ${r.data.company_name || 'Reno Resolve'}`;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const setDocState = (docType, patch) => {
    setUploads((prev) => ({ ...prev, [docType]: { ...(prev[docType] || {}), ...patch } }));
  };

  const handleUpload = async (docType) => {
    const st = uploads[docType] || {};
    if (!st.file) { toast.error('Sélectionnez un fichier'); return; }
    if (!st.editionDate) { toast.error("Renseignez la date d'édition"); return; }
    setDocState(docType, { sending: true });
    try {
      const fd = new FormData();
      fd.append('file', st.file);
      fd.append('doc_type', docType);
      fd.append('edition_date', st.editionDate);
      await axios.post(`${BACKEND_URL}/api/st-renewal/${token}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocState(docType, { sending: false, done: true, file: null });
      toast.success('Document reçu — merci !');
      await load();
    } catch (e) {
      setDocState(docType, { sending: false });
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-xl border border-slate-200 shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-slate-900 mb-2">Lien invalide ou expiré</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">
            Contactez votre donneur d&apos;ordre pour obtenir un nouveau lien.
          </p>
        </div>
      </div>
    );
  }

  const alreadyUploaded = new Set((info?.already_uploaded || []).map((u) => u.doc_type));
  const allDone = (info?.required_documents || []).every((d) => alreadyUploaded.has(d.type));

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-900 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">{info.company_name}</h1>
              <p className="text-sm text-blue-100">Dépôt sécurisé de vos documents à jour</p>
            </div>
          </div>
          <p className="text-sm text-blue-100 mt-3">
            Bonjour <b>{info.sous_traitant_nom}</b>, merci de déposer ci-dessous les documents demandés.
            L&apos;intégration à notre CRM est automatique.
          </p>
          {info.expires_at && (
            <p className="text-xs text-blue-200 mt-2">
              Ce lien est valide jusqu&apos;au {formatDate(info.expires_at)}.
            </p>
          )}
        </div>

        {info.custom_message && (
          <div
            className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 shadow-sm"
            data-testid="renewal-custom-message"
          >
            <div className="text-xs uppercase tracking-wider font-bold text-amber-800 mb-1">
              Message de votre donneur d&apos;ordre
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
              {info.custom_message}
            </p>
          </div>
        )}

        {allDone && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              Tous les documents demandés ont été transmis. Merci !
            </p>
          </div>
        )}

        {/* Document cards */}
        <div className="space-y-3">
          {(info.required_documents || []).map((d) => {
            const state = uploads[d.type] || {};
            const already = alreadyUploaded.has(d.type);
            return (
              <div
                key={d.type}
                className={`bg-white rounded-xl border p-5 shadow-sm ${
                  already ? 'border-green-200 bg-green-50/30' : 'border-slate-200'
                }`}
                data-testid={`renewal-doc-${d.type}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    already ? 'bg-green-100' : 'bg-blue-50'
                  }`}>
                    {already ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{d.label}</h3>
                    {d.max_age_days && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Doit dater de moins de {d.max_age_days} jours
                      </p>
                    )}
                    {already && (
                      <p className="text-xs text-green-700 mt-1 font-medium">✓ Bien reçu</p>
                    )}
                  </div>
                </div>

                {!already && (
                  <div className="mt-4 space-y-3 pl-13">
                    <div>
                      <Label htmlFor={`date-${d.type}`} className="text-xs">Date d&apos;édition sur le document</Label>
                      <Input
                        id={`date-${d.type}`}
                        type="date"
                        value={state.editionDate || ''}
                        onChange={(e) => setDocState(d.type, { editionDate: e.target.value })}
                        max={new Date().toISOString().slice(0, 10)}
                        data-testid={`renewal-date-${d.type}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`file-${d.type}`} className="text-xs">Fichier (PDF, JPG, PNG · max 12 Mo)</Label>
                      <Input
                        id={`file-${d.type}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                        onChange={(e) => setDocState(d.type, { file: e.target.files?.[0] || null })}
                        data-testid={`renewal-file-${d.type}`}
                      />
                      {state.file && (
                        <p className="text-xs text-slate-500 mt-1">
                          {state.file.name} · {(state.file.size / 1024).toFixed(0)} Ko
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleUpload(d.type)}
                      disabled={!state.file || !state.editionDate || state.sending}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid={`renewal-submit-${d.type}`}
                    >
                      {state.sending
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours…</>
                        : <><Upload className="w-4 h-4 mr-2" />Envoyer ce document</>}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 text-center">
          Vos documents sont chiffrés en transit (HTTPS) et stockés de manière sécurisée.
        </p>
      </div>
    </div>
  );
}
